/*
 * Convert to Infographic — deck writeback.
 *
 * Two shapes of result reach here:
 *   - Standalone ("full_slide", no merge): the backend's inner <p:spTree>
 *     shape XML wrapped into a fresh one-slide .pptx (PptxGenJS skeleton +
 *     fflate slide1.xml swap, same recipe as MapPlot's buildMapSlide).
 *   - Merged ("region"): a full clone of the active deck with one slide's
 *     XML modified in place (infographicRegionMerge.ts), meant to be
 *     inserted via `sourceSlideIds` so only that one slide comes in.
 * Both are inserted right after the current slide via
 * insertSlidesFromBase64 (parity port of the DecKorate POC's insertion
 * step, including its "retry once without the sourceSlideIds filter"
 * recovery when the first attempt is rejected).
 */

import PptxGenJS from "pptxgenjs";
import { strToU8, unzipSync, zipSync } from "fflate";
import { NS_A, NS_P, NS_R } from "../../../const";
import { hasPpt, ppt } from "../../../utils/formatUtils";
import { logInfographicError } from "./infographicApi";
import type { SlideDimensions } from "../../create-slide/service/slideGenerationApi";

/** EMU per inch — the fixed OOXML unit conversion (also = 72 points/inch *
 *  12700 EMU/point), used to size the skeleton to the live deck's own slide
 *  dimensions instead of a hardcoded 16:9 (mismatched dimensions between the
 *  inserted file and the destination presentation are a plausible cause of
 *  an outright insertSlidesFromBase64 rejection). */
const EMU_PER_INCH = 914400;
const CUSTOM_LAYOUT_NAME = "INFOGRAPHIC_SLIDE";

/** Thrown when the backend returned no usable shape XML — distinct from an
 *  HTTP/network failure, since the request itself succeeded (200/valid=false). */
export const NO_INFOGRAPHIC_XML_MESSAGE =
  "The infographic service didn't return a usable layout. Nothing was changed — try again.";

/** Fallback header used only if the backend's xml is ever missing its own
 *  (per the OOXML Composer prompt, it always starts with one). Without this,
 *  prepending our own header whenever the backend's is already present would
 *  leave <p:spTree> with two <p:nvGrpSpPr>/<p:grpSpPr> pairs — invalid OOXML
 *  that PowerPoint's insert-slides call rejects outright (InvalidArgument). */
const GROUP_HEADER_MARKER = "<p:nvGrpSpPr";
const FALLBACK_GROUP_HEADER =
  `<p:nvGrpSpPr><p:cNvPr id="1" name=""/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>` + `<p:grpSpPr/>`;

/** OOXML descendants the composer's own system prompt calls out as "will
 *  crash slide insertion" (see slide_redesign_ooxml.py's "FORBIDDEN OOXML
 *  ELEMENTS" section) — but the backend's validation step only checks
 *  well-formedness and shape-tag presence, so a non-compliant AI response
 *  still comes back `valid: true` and reaches the client unfiltered. */
const CRASH_PRONE_DESCENDANTS = ["effectLst", "outerShdw", "gradFill", "scene3d", "sp3d", "extLst", "custGeom"];

/** Below this, the same prompt says a solid <a:ln> width also crashes insertion. */
const MIN_LINE_WIDTH_EMU = 12700;

/** CT_ShapeProperties' required child sequence (ECMA-376 §20.1.2.2.35): xfrm,
 *  geometry, fill, then ln — anything out of this order is schema-invalid and
 *  PowerPoint's insertSlidesFromBase64 rejects it outright (InvalidArgument)
 *  even though a well-formedness-only check (the backend's own validation)
 *  passes it. Observed in the wild on composer-generated <p:cxnSp> shapes,
 *  which came back with <a:ln> before <a:noFill/> — every <p:sp> in the same
 *  response had the correct fill-before-ln order, so this is specific to how
 *  the composer emits connectors. CRASH_PRONE_DESCENDANTS above already
 *  strips the effect/scene3d/sp3d/extLst tail of the sequence, so only these
 *  four need reordering. */
const SHAPE_PROPERTY_ORDER = ["xfrm", "custGeom", "prstGeom", "noFill", "solidFill", "gradFill", "blipFill", "pattFill", "grpFill", "ln"];

/** Reorders each <p:spPr>/<p:grpSpPr>'s direct children back into the schema
 *  sequence above, in place. Unrecognised children (there shouldn't be any
 *  left after the strip pass) sort after everything else rather than being
 *  dropped. */
function reorderShapeProperties(doc: Document): void {
  const containers = [
    ...Array.from(doc.getElementsByTagNameNS(NS_P, "spPr")),
    ...Array.from(doc.getElementsByTagNameNS(NS_P, "grpSpPr")),
  ];
  for (const container of containers) {
    const children = Array.from(container.children);
    const ordered = children
      .map((child, index) => ({ child, index }))
      .sort((a, b) => {
        const rank = (el: Element) => {
          const i = SHAPE_PROPERTY_ORDER.indexOf(el.localName);
          return i === -1 ? SHAPE_PROPERTY_ORDER.length : i;
        };
        return rank(a.child) - rank(b.child) || a.index - b.index;
      })
      .map(({ child }) => child);
    if (ordered.some((child, i) => child !== children[i])) {
      for (const child of ordered) container.appendChild(child);
    }
  }
}

/**
 * Defensive repair pass over the AI composer's shape XML, run before every
 * insertion path (standalone wrap and region merge alike): strips the
 * constructs above, drops any <p:pic> (this pipeline never supplies the
 * image relationship one would need to resolve), clamps sub-minimum line
 * widths, fixes shape-property child ordering, and renumbers every shape id
 * from `startId` so it can't collide with another shape sharing the slide —
 * the wrapper's own header, another AI shape, or (for a region merge)
 * whatever's left of the original slide. Malformed input is returned
 * untouched — insertion fails loudly on it rather than this silently
 * mangling something already broken.
 */
export function sanitizeComposerXml(spTreeXml: string, startId = 1): string {
  if (!spTreeXml) return spTreeXml;
  const doc = new DOMParser().parseFromString(
    `<root xmlns:a="${NS_A}" xmlns:r="${NS_R}" xmlns:p="${NS_P}">${spTreeXml}</root>`,
    "application/xml",
  );
  if (doc.getElementsByTagName("parsererror").length > 0) {
    logInfographicError("infographic-sanitize-parse-failed", new Error("Composer shape XML failed to parse"));
    return spTreeXml;
  }

  for (const name of CRASH_PRONE_DESCENDANTS) {
    for (const el of Array.from(doc.getElementsByTagNameNS(NS_A, name))) el.remove();
  }
  for (const pic of Array.from(doc.getElementsByTagNameNS(NS_P, "pic"))) pic.remove();
  for (const ln of Array.from(doc.getElementsByTagNameNS(NS_A, "ln"))) {
    const width = Number(ln.getAttribute("w"));
    if (Number.isFinite(width) && width < MIN_LINE_WIDTH_EMU) ln.setAttribute("w", String(MIN_LINE_WIDTH_EMU));
  }
  reorderShapeProperties(doc);

  let nextId = startId;
  for (const cNvPr of Array.from(doc.getElementsByTagNameNS(NS_P, "cNvPr"))) {
    cNvPr.setAttribute("id", String(nextId++));
  }

  const serializer = new XMLSerializer();
  return Array.from(doc.documentElement.childNodes)
    .map((node) => serializer.serializeToString(node))
    .join("");
}

function wrapSpTreeIntoSlideXml(spTreeXml: string): string {
  const rawContent = spTreeXml.includes(GROUP_HEADER_MARKER) ? spTreeXml : FALLBACK_GROUP_HEADER + spTreeXml;
  const spTreeContent = sanitizeComposerXml(rawContent);
  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<p:sld xmlns:a="${NS_A}" xmlns:r="${NS_R}" xmlns:p="${NS_P}">` +
    `<p:cSld><p:spTree>` +
    spTreeContent +
    `</p:spTree></p:cSld>` +
    `<p:clrMapOvr><a:masterClrMapping/></p:clrMapOvr>` +
    `</p:sld>`
  );
}

/** Uint8Array -> base64, chunked to stay within the call-argument limit. */
export function u8ToBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCodePoint(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** Builds a one-slide base64 .pptx wrapping the backend's shape XML — used
 *  for the "full_slide" (no region to merge into) path. Sized to match the
 *  live deck exactly, so the destination presentation never has to reconcile
 *  a differently-sized inserted slide. */
export async function buildInfographicSlideBase64(spTreeXml: string, dims: SlideDimensions): Promise<string> {
  const xml = wrapSpTreeIntoSlideXml(spTreeXml);

  const pptx = new PptxGenJS();
  pptx.defineLayout({ name: CUSTOM_LAYOUT_NAME, width: dims.cx / EMU_PER_INCH, height: dims.cy / EMU_PER_INCH });
  pptx.layout = CUSTOM_LAYOUT_NAME;
  pptx.addSlide();
  const skeletonBuf = (await pptx.write({ outputType: "arraybuffer" })) as ArrayBuffer;

  const entries = unzipSync(new Uint8Array(skeletonBuf));
  entries["ppt/slides/slide1.xml"] = strToU8(xml);

  return u8ToBase64(zipSync(entries, { level: 6 }));
}

interface InsertSlidesFromBase64Options {
  formatting: "UseDestinationTheme" | "KeepSourceFormatting";
  targetSlideId?: string;
  sourceSlideIds?: string[];
}

type PresentationWithBase64Insert = PowerPoint.Presentation & {
  insertSlidesFromBase64?: (base64: string, options?: InsertSlidesFromBase64Options) => void;
};

/** The active slide's own id, so the result can be targeted to land right
 *  after it (parity: the POC always inserts relative to the slide the
 *  operation was started from, not necessarily the end of the deck). */
async function getActiveSlideOfficeId(context: PowerPoint.RequestContext): Promise<string | undefined> {
  const selectedSlides = context.presentation.getSelectedSlides();
  selectedSlides.load("items/id");
  await context.sync();
  return selectedSlides.items[0]?.id;
}

/** Office/RichApi errors carry their real explanation in `.message` and
 *  `.debugInfo`, separate from the terse `.code`/`.name` (e.g. "InvalidArgument")
 *  that ends up as `Error.message` when the SDK's own Error subclass is thrown
 *  with nothing more specific — surface all of it so a failure is diagnosable
 *  instead of showing just the bare code. */
function describeOfficeError(error: unknown): string {
  if (!error || typeof error !== "object") return String(error);
  const err = error as {
    name?: string;
    code?: string;
    message?: string;
    debugInfo?: { message?: string; errorLocation?: string };
  };
  const parts = [err.code ?? err.name, err.message, err.debugInfo?.message, err.debugInfo?.errorLocation].filter(
    (part): part is string => typeof part === "string" && part.length > 0,
  );
  if (parts.length > 0) return Array.from(new Set(parts)).join(" — ");
  try {
    return JSON.stringify(error);
  } catch {
    return "Unknown error";
  }
}

/** One `PowerPoint.run` attempt at the insert. Split out so a failed sync
 *  never leaves a retry re-using (and possibly hitting) the same broken
 *  request queue — each attempt opens a fresh context. */
async function attemptInsert(base64Pptx: string, options: InsertSlidesFromBase64Options): Promise<void> {
  try {
    await ppt().run(async (context) => {
      const presentation = context.presentation as PresentationWithBase64Insert;
      if (typeof presentation.insertSlidesFromBase64 !== "function") {
        throw new TypeError("This PowerPoint version can't insert slides from base64 (needs PowerPointApi 1.2+).");
      }
      const targetSlideId = options.targetSlideId ?? (await getActiveSlideOfficeId(context));
      presentation.insertSlidesFromBase64(base64Pptx, { ...options, ...(targetSlideId ? { targetSlideId } : {}) });
      await context.sync();
    });
  } catch (error) {
    throw new Error(describeOfficeError(error));
  }
}

/**
 * Inserts `base64Pptx` right after the active slide. When `sourceSlideId` is
 * set, only that slide is pulled from the (whole-deck-clone) source pptx; if
 * PowerPoint rejects the request with that filter applied, retries once
 * (in a fresh batch) without it before giving up — mirrors the POC's
 * one-shot recovery instead of failing outright on a filter PowerPoint
 * didn't like.
 */
export async function insertInfographicResult(base64Pptx: string, sourceSlideId: string | null): Promise<void> {
  if (!hasPpt()) throw new Error("PowerPoint API unavailable — not running inside PowerPoint.");

  const baseOptions: InsertSlidesFromBase64Options = { formatting: "UseDestinationTheme" };
  if (!sourceSlideId) {
    await attemptInsert(base64Pptx, baseOptions);
    return;
  }

  try {
    await attemptInsert(base64Pptx, { ...baseOptions, sourceSlideIds: [sourceSlideId] });
  } catch (filteredErr) {
    // The sourceSlideIds filter can be rejected on some hosts — retry once
    // without it, logging first so a successful retry doesn't hide the failure.
    logInfographicError("infographic-insert-filtered-retry", filteredErr);
    await attemptInsert(base64Pptx, baseOptions);
  }
}
